/**
 * Effective-dated holder reassignment dialog.
 *
 * Reassigns *who holds* an account — switch the client party, or flip
 * self-held ↔ client-held — as of a chosen date. The backend closes the
 * current rate/holder window at that date and opens a new one, so old
 * settlements and history keep their original client and rates; only
 * entries from the effective date forward use the new holder.
 *
 * PIN-gated: the PATCH may return 423, which `withPin` catches and retries
 * after the PIN modal unlocks.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import type { Account, Party, RateChangeResponse } from '../api';
import { usePinGate } from '../auth/pinGate';
import {
  Button,
  Checkbox,
  ErrorBanner,
  Select,
  Spinner,
  TextInput,
} from '../ui/primitives';
import { Modal } from '../ui/Modal';

export interface ChangeHolderDialogProps {
  account: Account;
  clients: Party[];
  /** Current upline rate as a multiplier string, e.g. "0.06" or "-0.05". */
  currentUplineRate: string;
  /** Current holder; null = self-held. */
  currentClientPartyId: number | null;
  onClose: () => void;
  onSaved: () => void;
}

/** Percent string (UI) → decimal multiplier string (backend). */
const pctToRate = (p: string) => {
  const n = Number(p);
  return Number.isFinite(n) ? String(+(n / 100).toFixed(8)) : p.trim();
};

/** Multiplier string (backend) → percent string for the input default. */
const rateToPctInput = (r: string) => {
  const n = Number(r);
  return Number.isFinite(n) ? String(+(n * 100).toFixed(4)) : '';
};

/** Today's date (YYYY-MM-DD) in Asia/Kolkata, independent of host TZ. */
function todayInKolkata(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

export function ChangeHolderDialog({
  account,
  clients,
  currentUplineRate,
  currentClientPartyId,
  onClose,
  onSaved,
}: ChangeHolderDialogProps) {
  const qc = useQueryClient();
  const { withPin } = usePinGate();

  const [selfHeld, setSelfHeld] = useState(currentClientPartyId === null);
  const [client, setClient] = useState<string>(
    currentClientPartyId === null ? '' : String(currentClientPartyId),
  );
  const [clientRate, setClientRate] = useState('');
  const [uplineRate, setUplineRate] = useState(rateToPctInput(currentUplineRate));
  const [effectiveFrom, setEffectiveFrom] = useState(todayInKolkata());
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      api.patch<RateChangeResponse>(`/api/accounts/${account.id}/holder`, {
        clientPartyId: selfHeld ? null : Number(client),
        clientRate: selfHeld ? null : pctToRate(clientRate),
        uplineRate: pctToRate(uplineRate),
        effectiveFrom,
      }),
    onSuccess: () => {
      void qc.invalidateQueries();
      onSaved();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!selfHeld && !client) {
      setError('Pick a client, or tick "Self-held".');
      return;
    }
    if (!uplineRate.trim()) {
      setError('Upline rate is required.');
      return;
    }
    if (!selfHeld && !clientRate.trim()) {
      setError('Client rate is required when assigning to a client.');
      return;
    }
    if (!effectiveFrom) {
      setError('Effective-from date is required.');
      return;
    }

    void withPin(() => mut.mutateAsync());
  };

  const clientOptions = clients.map((c) => ({
    value: String(c.id),
    label: c.displayName,
  }));

  return (
    <Modal onClose={onClose}>
        <header className="flex items-center justify-between border-b border-border bg-surface-raised px-4 py-3">
          <h2 className="text-small font-semibold text-ink">Change holder</h2>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </header>

        <form className="flex-1 overflow-y-auto p-4" onSubmit={onSubmit}>
          <p className="mb-3 rounded-md border border-border bg-surface-raised px-3 py-2 text-caption text-ink-muted">
            Reassigns <span className="font-medium text-ink">{account.canonicalName}</span> from the
            effective date forward. Past weeks keep their existing client and rates — old entries keep
            old values, new entries take new.
          </p>

          <div className="space-y-3">
            <Checkbox
              label="Self-held (no client — you hold this account)"
              checked={selfHeld}
              onChange={(next) => {
                setSelfHeld(next);
                if (next) {
                  setClient('');
                  setClientRate('');
                }
              }}
            />

            {!selfHeld && (
              <>
                <Select
                  label="Client"
                  value={client}
                  onChange={(v) => setClient(v)}
                  options={[{ value: '', label: '— select client —' }, ...clientOptions]}
                  hint="Who holds the account from the effective date onward."
                />
                <TextInput
                  label="Client rate %"
                  value={clientRate}
                  onChange={(e) => setClientRate(e.target.value)}
                  inputMode="text"
                  placeholder="e.g. 5 or -2.5"
                  hint="Percent. Negative or zero allowed."
                  name="client-rate"
                />
              </>
            )}

            <TextInput
              label="Upline rate %"
              value={uplineRate}
              onChange={(e) => setUplineRate(e.target.value)}
              inputMode="text"
              placeholder="e.g. 6 or -5"
              hint="Percent. Negative or zero allowed."
              name="upline-rate"
            />

            <TextInput
              label="Effective from"
              type="date"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
              hint="Applies from this date forward. Must be strictly after the current window's start date."
              name="effective-from"
            />
          </div>

          {error && (
            <div className="mt-3">
              <ErrorBanner>{error}</ErrorBanner>
            </div>
          )}

          <div className="mt-4 flex items-center gap-2">
            <Button type="submit" disabled={mut.isPending}>
              {mut.isPending ? <Spinner /> : 'Apply change'}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
    </Modal>
  );
}
